import { clearSessionProfile, notifyAuthSessionChanged } from "./sessionProfile";

export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:8080").replace(/\/$/, "");
export const AUTH_TOKEN_KEY = "autotuber_auth_token";
export const AUTH_USERNAME_KEY = "autotuber_username";
export const AUTH_ROLE_KEY = "autotuber_role";
export const AUTH_STATUS_KEY = "autotuber_status";

const textDecoder = typeof TextDecoder !== "undefined" ? new TextDecoder("utf-8") : null;
const WINDOWS_1252_BYTES = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87,
  0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e,
  0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};

function mojibakeScore(value) {
  return (value.match(/[ÃÂâÄÅ]/g) || []).length + (value.match(/[\u0080-\u009f]/g) || []).length * 2;
}

function toLegacyBytes(value) {
  const bytes = [];
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 0xff) bytes.push(code);
    else if (WINDOWS_1252_BYTES[code]) bytes.push(WINDOWS_1252_BYTES[code]);
    else return null;
  }
  return new Uint8Array(bytes);
}

export function repairMojibakeText(value) {
  if (typeof value !== "string" || !textDecoder || !/[ÃÂâÄÅ]/.test(value)) return value;
  let current = value;
  for (let index = 0; index < 4; index += 1) {
    const bytes = toLegacyBytes(current);
    if (!bytes) break;
    const next = textDecoder.decode(bytes);
    if (!next || next === current || mojibakeScore(next) > mojibakeScore(current)) break;
    current = next;
  }
  return current;
}

function repairMojibakeDeep(value) {
  if (typeof value === "string") return repairMojibakeText(value);
  if (Array.isArray(value)) return value.map(repairMojibakeDeep);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, repairMojibakeDeep(item)]));
  }
  return value;
}

function getTokenPayload(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      Array.prototype.map
        .call(atob(padded), (char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

export function isTokenExpired(token = localStorage.getItem(AUTH_TOKEN_KEY)) {
  if (!token) return true;
  const payload = getTokenPayload(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 <= Date.now();
}

export function clearAuthSession() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USERNAME_KEY);
  localStorage.removeItem(AUTH_ROLE_KEY);
  localStorage.removeItem(AUTH_STATUS_KEY);
  clearSessionProfile();
  notifyAuthSessionChanged();
}

export function isAdminRole(role = localStorage.getItem(AUTH_ROLE_KEY)) {
  return role === "SUPER_ADMIN" || role === "ADMIN";
}

export function hasValidAuthToken() {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) return false;
  if (isTokenExpired(token)) {
    clearAuthSession();
    return false;
  }
  return true;
}

function redirectToLogin() {
  const publicAuthPath = window.location.pathname.startsWith("/sign-in") || window.location.pathname.startsWith("/sign-up");
  if (!publicAuthPath) window.location.assign("/sign-in");
}

export async function apiRequest(path, options = {}) {
  let token = localStorage.getItem(AUTH_TOKEN_KEY);
  const isAuthRequest = path.startsWith("/api/auth/");
  if (token && isTokenExpired(token)) {
    clearAuthSession();
    token = null;
    if (!isAuthRequest) {
      redirectToLogin();
      throw new Error("Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại");
    }
  }
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = { ...(options.body && !isFormData ? { "Content-Type": "application/json; charset=utf-8" } : {}), ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  const requestBody = options.body && !isFormData && typeof options.body !== "string" ? JSON.stringify(repairMojibakeDeep(options.body)) : options.body;
  const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers, body: requestBody });
  const text = await response.text(); let data = text;
  try { data = text ? JSON.parse(text) : null; } catch (_) { /* text response */ }
  if (response.status === 401 || response.status === 403) {
    clearAuthSession();
    redirectToLogin();
  }
  if (!response.ok) throw new Error(typeof data === "string" ? data : data?.message || data?.error || `API error ${response.status}`);
  return repairMojibakeDeep(data);
}

export async function apiDownload(path) {
  let token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token && isTokenExpired(token)) {
    clearAuthSession();
    redirectToLogin();
    throw new Error("Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại");
  }
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (response.status === 401 || response.status === 403) {
    clearAuthSession();
    redirectToLogin();
  }
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `API error ${response.status}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return { blob, fileName: match?.[1] || "worker-agent.zip" };
}

export const api = {
  get: (path) => apiRequest(path),
  post: (path, body) => apiRequest(path, { method: "POST", body }),
  put: (path, body) => apiRequest(path, { method: "PUT", body }),
  putMultipart: (path, formData) => apiRequest(path, { method: "PUT", body: formData }),
  upload: (path, formData) => apiRequest(path, { method: "POST", body: formData }),
  download: (path) => apiDownload(path),
  delete: (path) => apiRequest(path, { method: "DELETE" }),
};
